import React, { useEffect, useState } from 'react';
import type { CatalogScope, LeaseChoice, TransferMode } from '@platform/shared-types';
import SearchableDropdown from '../../../components/SearchableDropdown';
import { api } from '../../../services/apiService';
import { businessTransferApi } from '../../../services/businessTransferApi';
import { toastService } from '../../../services/toastService';
import { useIsSuperAdmin } from '../useIsSuperAdmin';

export default function NewTransferModal({ onClose, onCreated }: { onClose: () => void; onCreated: (transferId: string) => void }) {
  const canAct = useIsSuperAdmin();
  const [mode, setMode] = useState<TransferMode>('store');
  const [sellerOptions, setSellerOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [sellerTenantId, setSeller] = useState('');
  const [locations, setLocations] = useState<Array<{ value: string; label: string }>>([]);
  const [locationId, setLocation] = useState('');
  const [buyerName, setBuyerName] = useState('');
  const [buyerEmail, setBuyerEmail] = useState('');
  const [catalogScope, setScope] = useState<CatalogScope>('priced_at_location');
  const [leaseDefault, setLease] = useState<LeaseChoice>('payoff');
  const [busy, setBusy] = useState(false);

  // Same tenant list call used by src/pages/Tenants.tsx (`api.tenants.getAll()`).
  useEffect(() => {
    api.tenants.getAll()
      .then((tenants) => setSellerOptions(tenants.map((t) => ({ value: t.id, label: t.businessName || t.name }))))
      .catch((e: any) => toastService.error(e.message || 'Could not load businesses'));
  }, []);
  useEffect(() => {
    if (!sellerTenantId) return;
    businessTransferApi.sellerLocations(sellerTenantId)
      .then((r) => setLocations(r.locations.filter((l) => l.status !== 'transferred').map((l) => ({ value: l.locationId, label: l.name }))))
      .catch((e: any) => toastService.error(e.message));
  }, [sellerTenantId]);

  const submit = async () => {
    setBusy(true);
    try {
      const d = await businessTransferApi.create({ mode, sellerTenantId, locationId: mode === 'store' ? locationId : undefined, buyerName, buyerEmail, catalogScope, leaseDefault });
      toastService.success(`${d.transfer.ref} created — invite sent to the buyer`);
      onCreated(d.transfer.transferId);
    } catch (e: any) { toastService.error(e.message); } finally { setBusy(false); }
  };

  const pill = (active: boolean) => `min-h-[44px] rounded-full px-4 text-sm font-medium ${active ? 'bg-emerald-600 text-white' : 'text-stone-600'}`;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="flex w-full max-w-lg flex-col gap-4 rounded-2xl bg-white p-5 shadow-xl">
        <h2 className="text-lg font-semibold">New business transfer</h2>
        {!canAct && <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">Only a super admin can create a transfer. You can view but not submit this form.</div>}
        <div className="inline-flex self-start rounded-full border border-stone-200 p-1">
          <button type="button" className={pill(mode === 'store')} onClick={() => setMode('store')}>One store</button>
          <button type="button" className={pill(mode === 'business')} onClick={() => setMode('business')}>Whole business</button>
        </div>
        <SearchableDropdown label="Seller business" options={sellerOptions} value={sellerTenantId} onChange={(v) => setSeller(v ?? '')} placeholder="Search businesses" />
        {mode === 'store' && (
          <SearchableDropdown label="Store being sold" options={locations} value={locationId} onChange={(v) => setLocation(v ?? '')} placeholder="Choose a store" disabled={!sellerTenantId} />
        )}
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          <input value={buyerName} onChange={(e) => setBuyerName(e.target.value)} placeholder="Buyer's name" className="min-h-[44px] rounded-lg border border-stone-300 px-3" />
          <input value={buyerEmail} onChange={(e) => setBuyerEmail(e.target.value)} placeholder="Buyer's email" type="email" className="min-h-[44px] rounded-lg border border-stone-300 px-3" />
        </div>
        {mode === 'store' && (
          <>
            <div className="inline-flex self-start rounded-full border border-stone-200 p-1">
              <button type="button" className={pill(catalogScope === 'priced_at_location')} onClick={() => setScope('priced_at_location')}>Priced at this store</button>
              <button type="button" className={pill(catalogScope === 'whole_catalog')} onClick={() => setScope('whole_catalog')}>Whole catalog</button>
            </div>
            <div className="inline-flex self-start rounded-full border border-stone-200 p-1">
              <button type="button" className={pill(leaseDefault === 'payoff')} onClick={() => setLease('payoff')}>Seller pays off leases</button>
              <button type="button" className={pill(leaseDefault === 'takeover')} onClick={() => setLease('takeover')}>Buyer takes over</button>
            </div>
          </>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="min-h-[44px] rounded-lg border border-stone-300 px-4">Cancel</button>
          <button type="button" disabled={!canAct || busy || !sellerTenantId || !buyerName || !buyerEmail || (mode === 'store' && !locationId)} onClick={submit} className="min-h-[44px] rounded-lg bg-[#DC143C] px-4 font-semibold text-white disabled:opacity-40">Create and invite buyer</button>
        </div>
      </div>
    </div>
  );
}
